import React, { useState, useEffect } from 'react';
import { classAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { UsersRound, Wallet, Zap, Coins, Star, Crown, Receipt, CheckCircle2, Clock, ShoppingCart } from 'lucide-react';
import { formatCoins } from '../../lib/utils';

function timeLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}

export default function GroupDetailDialog({ open, onClose, classId, groupId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && classId && groupId) {
      setLoading(true);
      classAPI.getGroupHistory(classId, groupId)
        .then(res => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, classId, groupId]);

  if (!open) return null;

  // Separate transactions into earnings and purchases
  const purchases = (data?.transactions || []).filter(t => t.reference_type === 'product_purchase' || t.amount < 0);
  const earnings = (data?.transactions || []).filter(t => t.reference_type !== 'product_purchase' && t.amount >= 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="group-detail-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${data?.group_color || '#3b82f6'}20` }}>
              <UsersRound className="w-4 h-4" style={{ color: data?.group_color || '#3b82f6' }} />
            </div>
            {data?.group_name || 'Grupo'}
            {data?.leader_name && (
              <Badge className="bg-amber-50 text-amber-700 text-xs ml-1"><Crown className="w-3 h-3 mr-1" />{data.leader_name}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              <div className="p-2 bg-blue-50 rounded-lg text-center">
                <Wallet className="w-4 h-4 text-blue-500 mx-auto mb-0.5" />
                <p className="text-base font-bold text-blue-700">{formatCoins(data.group_balance)}</p>
                <p className="text-[10px] text-blue-600">Wallet Grupal</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg text-center">
                <Coins className="w-4 h-4 text-amber-500 mx-auto mb-0.5" />
                <p className="text-base font-bold text-amber-700">{formatCoins(data.total_individual_coins)}</p>
                <p className="text-[10px] text-amber-600">Coins Individuales</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg text-center">
                <Zap className="w-4 h-4 text-purple-500 mx-auto mb-0.5" />
                <p className="text-base font-bold text-purple-700">{data.total_xp}</p>
                <p className="text-[10px] text-purple-600">XP Total</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-lg text-center">
                <UsersRound className="w-4 h-4 text-slate-500 mx-auto mb-0.5" />
                <p className="text-base font-bold text-slate-700">{data.member_count}</p>
                <p className="text-[10px] text-slate-600">Miembros</p>
              </div>
            </div>

            <Tabs defaultValue="members" className="flex flex-col flex-1 overflow-hidden">
              <TabsList className="grid grid-cols-4 mb-2">
                <TabsTrigger value="members" data-testid="group-tab-members">
                  <UsersRound className="w-3.5 h-3.5 mr-1" />Miembros
                </TabsTrigger>
                <TabsTrigger value="transactions" data-testid="group-tab-transactions">
                  <Receipt className="w-3.5 h-3.5 mr-1" />Ingresos ({earnings.length})
                </TabsTrigger>
                <TabsTrigger value="purchases" data-testid="group-tab-purchases">
                  <ShoppingCart className="w-3.5 h-3.5 mr-1" />Compras ({purchases.length})
                </TabsTrigger>
                <TabsTrigger value="completions" data-testid="group-tab-completions">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Actividades ({data.completions.length})
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto pr-1">
                {/* Members Tab */}
                <TabsContent value="members" className="mt-0">
                  <div className="space-y-2">
                    {data.members.map(m => (
                      <div key={m.user_id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid={`group-member-${m.user_id}`}>
                        <div className={`w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ${m.is_leader ? 'ring-amber-400' : 'ring-transparent'}`}>
                          {m.photo_url ? (
                            <img src={m.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm font-semibold text-slate-600">{m.name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-sm text-slate-900 truncate">{m.name}</span>
                            {m.is_leader && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-0.5"><Star className="w-3 h-3" />Nv. {m.level} {m.level_name}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <div>
                            <p className="text-sm font-semibold text-amber-600">{formatCoins(m.coins)}</p>
                            <p className="text-[10px] text-slate-400">Coins</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-purple-600">{m.xp}</p>
                            <p className="text-[10px] text-slate-400">XP</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Earnings Tab */}
                <TabsContent value="transactions" className="mt-0">
                  {earnings.length === 0 ? (
                    <div className="text-center py-10">
                      <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Sin ingresos grupales</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {earnings.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid={`group-earning-${i}`}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-green-100">
                            <Coins className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {t.description || 'Coins recibidos'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <Clock className="w-3 h-3" />
                              {timeLabel(t.created_at)}
                            </div>
                          </div>
                          <span className="text-sm font-bold text-green-600">
                            +{formatCoins(t.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Purchases Tab */}
                <TabsContent value="purchases" className="mt-0">
                  {purchases.length === 0 ? (
                    <div className="text-center py-10">
                      <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Sin compras grupales</p>
                      <p className="text-slate-400 text-xs mt-1">Las compras del marketplace usando el wallet grupal aparecen aqui</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {purchases.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid={`group-purchase-${i}`}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-red-100">
                            <ShoppingCart className="w-4 h-4 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {t.description || 'Compra grupal'}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <Clock className="w-3 h-3" />
                              {timeLabel(t.created_at)}
                            </div>
                          </div>
                          <span className="text-sm font-bold text-red-600">
                            {formatCoins(t.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Completions Tab */}
                <TabsContent value="completions" className="mt-0">
                  {data.completions.length === 0 ? (
                    <div className="text-center py-10">
                      <CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Sin actividades completadas</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {data.completions.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100" data-testid={`group-completion-${i}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                            c.is_group_activity ? 'bg-purple-100' : 'bg-slate-200'
                          }`}>
                            {c.is_group_activity ? (
                              <UsersRound className="w-4 h-4 text-purple-600" />
                            ) : c.student_photo ? (
                              <img src={c.student_photo} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs font-semibold text-slate-600">{c.student_name?.charAt(0)}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.activity_title}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                              {c.is_group_activity && c.member_count > 0 ? (
                                <>
                                  <Badge className="bg-purple-100 text-purple-700 text-[9px] px-1 h-4">Grupal</Badge>
                                  <span>{c.member_count} miembros</span>
                                </>
                              ) : (
                                <span>{c.student_name}</span>
                              )}
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{timeLabel(c.created_at)}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            {c.coins_earned > 0 && (
                              <p className="text-xs font-semibold text-amber-600">
                                +{formatCoins(c.coins_earned)}
                                {c.is_group_activity && <span className="text-[9px] text-purple-500 block">al grupo</span>}
                              </p>
                            )}
                            {c.xp_earned > 0 && <p className="text-[10px] text-purple-500">+{c.xp_earned} XP</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
